import React, { useState, useEffect, useMemo } from 'react';
import { adminService } from '@/services/admin-service';
import { authService } from '@/services/auth-service';
import { AppHeader } from '@/components/AppHeader';
import { Breadcrumb } from '@/components/Breadcrumb';
import { InlineList, InlineListRow } from '@/components/InlineList';
import { GradeBadge } from '@/components/GradeBadge';
import type { AdminConversationResponse, AdminSubjectsResponse } from '@/types/admin';
import type { User } from '@/types/auth';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './AdminDayConversations.css';
import './AdminUserConversations.css';
import '../styles/common.css';

interface SubjectScoreSummary {
  subjectId: string;
  subjectNumber: string;
  lastGrade?: number;
  bestGrade?: number;
}

interface ModuleConversationGroup {
  moduleId: string;
  moduleName: string;
  conversations: AdminConversationResponse[];
  subjectScores: SubjectScoreSummary[];
}

const getSubjectScores = (
  conversations: AdminConversationResponse[],
  subjects: AdminSubjectsResponse[]
): SubjectScoreSummary[] => {
  const scores = new Map<string, SubjectScoreSummary>(subjects.map(subject => [subject.id, {
    subjectId: subject.id,
    subjectNumber: subject.number,
  }]));

  conversations.forEach(conversation => {
    const score = scores.get(conversation.subjectId);
    if (!score) {
      scores.set(conversation.subjectId, {
        subjectId: conversation.subjectId,
        subjectNumber: conversation.subjectNumber,
        lastGrade: conversation.isAborted ? undefined : conversation.grade,
        bestGrade: conversation.isAborted ? undefined : conversation.grade,
      });
      return;
    }

    if (conversation.isAborted) {
      return;
    }
    if (score.lastGrade === undefined && conversation.grade !== undefined) {
      score.lastGrade = conversation.grade;
    }
    if (conversation.grade !== undefined && (score.bestGrade === undefined || conversation.grade > score.bestGrade)) {
      score.bestGrade = conversation.grade;
    }
  });

  return Array.from(scores.values()).sort((a, b) =>
    a.subjectNumber.localeCompare(b.subjectNumber, undefined, { numeric: true })
  );
};

const groupConversationsByModule = (
  conversations: AdminConversationResponse[],
  subjectsByModule: Record<string, AdminSubjectsResponse[]>
): ModuleConversationGroup[] => {
  const groups = new Map<string, { moduleName: string; conversations: AdminConversationResponse[] }>();

  conversations.forEach(conversation => {
    const moduleId = conversation.moduleId;
    const group = groups.get(moduleId);
    if (group) {
      group.conversations.push(conversation);
    } else {
      groups.set(moduleId, { moduleName: conversation.moduleName, conversations: [conversation] });
    }
  });

  return Array.from(groups.entries())
    .map(([moduleId, group]) => {
      const sortedConversations = group.conversations.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      return {
        moduleId,
        moduleName: group.moduleName,
        conversations: sortedConversations,
        subjectScores: getSubjectScores(sortedConversations, subjectsByModule[moduleId] ?? []),
      };
    })
    .sort((a, b) => a.moduleName.localeCompare(b.moduleName, undefined, { numeric: true }));
};

const SubjectScoresTable: React.FC<{ scores: SubjectScoreSummary[] }> = ({ scores }) => (
  <div className="subject-scores-table-wrapper">
    <table className="subject-scores-table">
      <thead>
        <tr>
          <th scope="col"></th>
          {scores.map(score => <th key={score.subjectId} scope="col">{score.subjectNumber}</th>)}
        </tr>
      </thead>
      <tbody>
        <tr>
          <th scope="row">Last</th>
          {scores.map(score => (
            <td key={score.subjectId}>{score.lastGrade !== undefined && <GradeBadge grade={score.lastGrade} />}</td>
          ))}
        </tr>
        <tr>
          <th scope="row">Best</th>
          {scores.map(score => (
            <td key={score.subjectId}>{score.bestGrade !== undefined && <GradeBadge grade={score.bestGrade} />}</td>
          ))}
        </tr>
      </tbody>
    </table>
  </div>
);

const AdminUserConversations: React.FC = () => {
  const [conversations, setConversations] = useState<AdminConversationResponse[]>([]);
  const [subjectsByModule, setSubjectsByModule] = useState<Record<string, AdminSubjectsResponse[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userFirstName, setUserFirstName] = useState('');
  const [userLastName, setUserLastName] = useState('');
  const [className, setClassName] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const userId = searchParams.get('userId');
  const classId = searchParams.get('classId');
  const from = sessionStorage.getItem('adminUserConversationsFrom') ?? 'modules';

  useEffect(() => {
    fetchUserInfo();
    if (userId) {
      fetchConversations();
      if (classId) {
        void fetchMetadata(userId, classId);
      }
    } else {
      setError('User ID is required');
      setLoading(false);
    }
  }, [userId, classId]);

  const fetchUserInfo = async () => {
    const currentUser = await authService.getCurrentUser();
    setUser(currentUser);
  };

  const fetchMetadata = async (targetUserId: string, targetClassId: string) => {
    try {
      const [classes, users] = await Promise.all([
        adminService.getAllClasses(),
        adminService.getUsersByClass(targetClassId),
      ]);
      const foundClass = classes.find(c => c.id === targetClassId);
      if (foundClass) setClassName(foundClass.name);
      const foundUser = users.find(u => u.id === targetUserId);
      if (foundUser) {
        setUserFirstName(foundUser.firstName);
        setUserLastName(foundUser.lastName);
      }
    } catch (err) {
      // Non-critical: page works without display names
    }
  };

  const fetchConversations = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getConversationsByUser(userId, classId ?? undefined);
      const moduleIds = Array.from(new Set(data.map(conversation => conversation.moduleId)));
      const subjectEntries = await Promise.all(moduleIds.map(async moduleId => (
        [moduleId, await adminService.getSubjectsByModule(moduleId)] as const
      )));
      setConversations(data);
      setSubjectsByModule(Object.fromEntries(subjectEntries));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };


  const handleViewConversation = (conv: AdminConversationResponse) => {
    sessionStorage.setItem('adminUserId', userId || '');
    sessionStorage.setItem('adminUserFirstName', userFirstName);
    sessionStorage.setItem('adminUserLastName', userLastName);
    sessionStorage.setItem('adminBackTo', 'user-conversations');
    sessionStorage.setItem('adminBackFrom', from);
    sessionStorage.setItem('adminBackUserId', userId || '');
    sessionStorage.setItem('adminBackUserFirstName', userFirstName);
    sessionStorage.setItem('adminBackUserLastName', userLastName);
    sessionStorage.setItem('adminBackClassId', classId || '');
    sessionStorage.setItem('adminBackClassName', className ?? '');
    navigate(`/admin/conversation?conversationId=${conv.id}`);
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleString('en-US', { month: 'long' });
    const year = d.getFullYear();
    const weekday = d.toLocaleString('en-US', { weekday: 'long' });
    return `${weekday}, ${month} ${day}, ${year}`;
  };

  const formatTime = (dateString: string) => {
    const d = new Date(dateString);
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const userName = [userFirstName, userLastName].filter(Boolean).join(' ');
  const moduleGroups = useMemo(
    () => groupConversationsByModule(conversations, subjectsByModule),
    [conversations, subjectsByModule]
  );

  return (
    <div className="admin-user-conversations-container">
      <AppHeader
        user={user}
        title="KI Expert - Admin"
        subtitle={userName ? `${className ? `${className} - ` : ''}Conversations by ${userName}` : 'Conversations'}
      />

      <main className="list-main">
        <div className="navigation-bar">
          <div className="navigation-bar-header">
            <Breadcrumb items={[
              from === 'classes' ? { label: 'Classes', path: '/admin/classes' } : { label: 'Modules', path: '/admin/modules' },
              { label: className ? `Users ${className}` : 'Users', path: `/admin/class-users?${new URLSearchParams({ ...(classId ? { classId } : {}), ...(className ? { className } : {}), from })}` },
              { label: userName ? `Conversations by ${userName}` : 'Conversations' },
            ]} />
            <h2 className="navigation-bar-title">{userName ? `${className ? `${className} - ` : ''}Conversations by ${userName}` : 'Conversations'}</h2>
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading conversations...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h3>Error loading</h3>
            <p>{error}</p>
            <button onClick={fetchConversations} className="btn btn-primary">
              Try again
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className="empty-container">
            <div className="empty-icon">💬</div>
            <h3>No conversations found</h3>
            <p>This user has not had any conversations yet.</p>
          </div>
        ) : (
          <div className="module-conversation-groups">
            {moduleGroups.map(group => (
              <section key={group.moduleId} className="module-conversation-group">
                <h3 className="module-conversation-title">{group.moduleName}</h3>
                <SubjectScoresTable scores={group.subjectScores} />
                <InlineList compact>
                  {group.conversations.map(conv => (
                    <InlineListRow
                      key={conv.id}
                      className={`${conv.isDiscussed ? 'discussed' : ''} ${conv.isAdjusted ? 'adjusted' : ''} ${conv.isAborted ? 'aborted' : ''}`}
                      actions={
                        <div className="conversation-status">
                          {conv.isAborted && <span className="status-badge aborted-badge">Aborted</span>}
                          <GradeBadge grade={conv.grade} />
                        </div>
                      }
                      onClick={() => handleViewConversation(conv)}
                    >
                      <span className="admin-subject-number">{conv.subjectNumber}</span>
                      <div className="admin-day-conv-info">
                        <h3 className="admin-day-conv-title">{conv.subjectTitle}</h3>
                        <span className="admin-day-conv-meta">{formatDate(conv.createdAt)} &middot; {formatTime(conv.createdAt)}</span>
                      </div>
                    </InlineListRow>
                  ))}
                </InlineList>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminUserConversations;
